import React, { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Divider,
    Link,
    Modal,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import EmailPlusIcon from 'Components/Icons/EmailPlusIcon';
import UserIcon from '../../../../../../../assets/images/users/Assignees.svg';
import { useMutation, useQuery } from '@apollo/client';
import { GET_AUTHORIZED_USERS, GET_FILE_URL, VERIFY_EMAIL_STATUS } from 'schemas/query';
import Loader from 'ui-component/Loader';
import { DISCONNECT_EMAIL, ENABLE_EMAIL_EXTENSION } from 'schemas/mutation';
import { useDispatch, useSelector } from 'react-redux';
import { SET_LOADING } from 'store/actions';
import { Outlet, useNavigate } from 'react-router';
import LocalStorage from 'utils/extension/localstorage';
import { checkIsPremiumPlan } from 'helper/checkPlan.helper';
import PremiumPage from '../WebForm/PremiumPage';
import Image from 'assets/images/dashboard/freemium-email-integration.png';
import { setCurrentPage } from 'store/ExecutiveTeam/actions';
import { getBrowserInfo } from 'utils/utils';
import PermisssionValidationWrapper from 'Components/PermissionWrapper/PermissionValidationWrapper';
import { PermissionTypes } from 'Components/PermissionWrapper/constants';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    maxWidth: '570px',
    bgcolor: '#fff',
    borderRadius: '12px',
    p: 3
};

const styles = {
    maxWidth: '746px',

    '& .MuiFormControl-root .MuiOutlinedInput-root': {
        background: 'transparent',
        '.MuiInputBase-input': {
            padding: '14.5px 10px 14.5px !important',
            fontWeight: '400',
            color: '#00214D',
            background: 'transparent',
            borderRadius: '8px'
        }
    },

    '& .choose-color': {
        '.ColorPicker-MuiInput-input': {
            display: 'none'
        },
        '.MuiFormControl-root': {
            width: '24px',
            height: '24px',

            '.MuiOutlinedInput-root .MuiInputAdornment-positionStart': {
                margin: '0'
            }
        },

        '& .MuiInputBase-colorPrimary': {
            padding: '0',
            width: '24px',
            height: '24px',

            '.MuiInputBase-input': {
                display: 'none'
            },

            '.MuiOutlinedInput-notchedOutline': {
                display: 'none'
            }
        },

        '.MuiButton-textPrimary': {
            border: '1px solid rgba(125, 125, 130, 0.25)',
            background: '#F9FAFB'
        }
    },

    '& .MuiButton-root': {
        borderRadius: '4px'
    },

    '.cancel': {
        background: '#7D7D82',
        border: '1px solid #7D7D82',
        color: '#fff',

        '&:hover': {
            background: 'transparent',
            borderColor: '#00214d',
            color: '#00214d'
        }
    },

    '.MuiAccordion-root': {
        margin: '0',
        background: 'none',
        '&:before': {
            display: 'none'
        },
        '& .MuiAccordionSummary': {
            '&-root': {
                minHeight: '0',
                padding: '0',
                flexDirection: 'row-reverse',
                gap: '10px'
            },
            '&-content': {
                margin: '0',
                fontSize: '1rem',
                fontWeight: '500',
                color: '#00214D'
            },
            '&-expandIconWrapper': {}
        }
    },

    '.MuiAccordionDetails-root': {
        padding: '24px 0'
    }
};

const EmailIntegrationComponent = () => {
    const { companyData } = useSelector((params) => params.customization);
    const isCompanyCreator = companyData.companyCreator;
    const isAccountPremium = checkIsPremiumPlan(companyData?.subscription?.plan);

    const token = LocalStorage.getAuthToken();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { data, loading, refetch } = useQuery(GET_AUTHORIZED_USERS, {
        context: {
            extToken: token
        }
    });

    useEffect(() => {
        dispatch({ type: setCurrentPage, currentPage: 'integrate-diolog' });
    }, []);

    const { data: userProfile } = useQuery(GET_FILE_URL, {
        variables: {
            key: data?.getAuthorizedUsers?.data?.profileImage
        },
        skip: !data?.getAuthorizedUsers?.data?.profileImage
    });

    const [addCompany] = useMutation(ENABLE_EMAIL_EXTENSION, {
        context: {
            extToken: token
        }
    });
    const [disconnectEmail] = useMutation(DISCONNECT_EMAIL, {
        context: {
            extToken: token
        }
    });
    const [url, setUrl] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [email, setEmail] = useState('');
    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => setModalOpen(false);

    const isEmailConnected = useMemo(() => {
        return data?.getAuthorizedUsers?.data?.users?.length > 0;
    }, [data?.getAuthorizedUsers?.data?.users]);

    useEffect(() => {
        if (isCompanyCreator) {
            addCompany();
        }
    }, [addCompany, isCompanyCreator]);

    useEffect(() => {
        if (userProfile) {
            setUrl(userProfile?.getSignedUrlFromKey?.signedUrl || '');
        }
    }, [userProfile]);

    const onDisconnect = async () => {
        try {
            dispatch({ type: SET_LOADING, loading: true });
            await disconnectEmail({ variables: { body: { email } } });
            await refetch({ fetchPolicy: 'network-only' });
            dispatch({ type: SET_LOADING, loading: false });
            handleModalClose();
        } catch (error) {
            dispatch({ type: SET_LOADING, loading: false });
            handleModalClose();
        }
    };

    const onDownload = () => {
        const browser = getBrowserInfo();
        window.open(browser, '_blank');
    };

    const onClickSignIn = () => navigate('/sign-in-extension', { replace: true });

    const hasData = !data || !data?.getAuthorizedUsers?.data?.users?.length;

    return (
        <>
            {loading && <Loader />}
            <Stack gap={3}>
                <Stack
                    sx={{
                        '@media (min-width: 768px)': {
                            flexDirection: 'row'
                        }
                    }}
                >
                    <Stack
                        gap={3}
                        sx={{
                            '@media (min-width: 768px)': {
                                width: 'calc(100% - 261px)',
                                paddingRight: '24px'
                            }
                        }}
                    >
                        <Stack gap={2}>
                            <Typography variant="body1" fontWeight="500" fontSize="1.5rem" color="#00214D" lineHeight="0.83333">
                                Diolog email integration extension
                            </Typography>
                            <Typography variant="body1" fontSize="0.875rem" color="#00214D" lineHeight="1.40476">
                                Send any authorised email content as questions straight to your Question Registry.
                            </Typography>
                            {!isAccountPremium && <Divider />}
                        </Stack>

                        {isAccountPremium && (
                            <Stack gap={2}>
                                <Typography variant="body1" fontSize="1rem" fontWeight="500" color="#00214D" lineHeight="1.25">
                                    Usage permissions
                                </Typography>
                                <Typography variant="body1" fontSize="0.875rem" color="#00214D" lineHeight="1.40476">
                                    Admins can control email integration permissions for each team member by navigating to:
                                </Typography>
                                <Typography variant="body1" fontSize="0.875rem" color="#00214D" lineHeight="1.40476">
                                    <Link href="/dashboard/my-team" color="inherit" sx={{ textDecoration: 'underline !important' }}>
                                        My team
                                    </Link>{' '}
                                    &gt; Select team member &gt; View Account Details &gt; Account permissions
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                    {isAccountPremium && (
                        <Box
                            sx={{
                                '@media (min-width: 768px)': {
                                    width: '261px'
                                }
                            }}
                        >
                            <Stack
                                sx={{
                                    border: '1px solid #DEDEDF',
                                    background: '#FAFAFC',
                                    borderRadius: '12px',
                                    padding: '24px'
                                }}
                                gap={3}
                            >
                                <Stack gap={2}>
                                    <Typography variant="body1" fontWeight="500" fontSize="1rem" color="#00214D" lineHeight="1.5">
                                        Available on
                                    </Typography>
                                    <Typography variant="body1" fontSize="0.875rem" color="#00214D" lineHeight="1.71429">
                                        Chrome, Edge, Mozilla Firefox
                                    </Typography>
                                </Stack>
                                <PermisssionValidationWrapper pageName={PermissionTypes.emailExtension}>
                                    <Stack gap={2}>
                                        <Button
                                            variant="outlined"
                                            className="alternate-reversed"
                                            disableRipple
                                            sx={{
                                                '&.alternate-reversed': {
                                                    backgroundColor: 'transparent'
                                                }
                                            }}
                                            onClick={() =>
                                                window.open(
                                                    'https://www.diolog.com.au/help/help-articles/how-to-set-up-the-diolog-email-extension'
                                                )
                                            }
                                        >
                                            View setup guide
                                        </Button>
                                        <Button size="large" variant="contained" className="alternate" disableRipple onClick={onDownload}>
                                            Download extension
                                        </Button>
                                    </Stack>
                                </PermisssionValidationWrapper>
                            </Stack>
                        </Box>
                    )}
                </Stack>
                {!isAccountPremium ? (
                    <Box sx={styles}>
                        <PremiumPage
                            title="Upgrade to access our email extension"
                            subTitle="Upgrade to access the ability to integrate with your email and send questions directly to your Question Registry."
                            image={Image}
                        />
                    </Box>
                ) : (
                    <Stack gap={3} alignItems="flex-start">
                        <Stack gap={2}>
                            <Typography variant="body1" fontWeight="500" fontSize="1rem" color="#00214D" lineHeight="1.25">
                                Authorised emails to collect questions from
                            </Typography>
                            <Typography variant="body1" fontSize="0.875rem" color="#00214D" lineHeight="1.40476">
                                Get started with email integration by authorising an email address from which you would like to collect
                                investor questions from.
                            </Typography>
                        </Stack>
                        <PermisssionValidationWrapper pageName={PermissionTypes.emailExtension}>
                            {!isEmailConnected && isCompanyCreator && (
                                <Button
                                    variant="outlined"
                                    className="alternate-reversed"
                                    startIcon={<EmailPlusIcon />}
                                    onClick={onClickSignIn}
                                >
                                    Connect an email
                                </Button>
                            )}
                        </PermisssionValidationWrapper>
                        <Box sx={{ width: '100%' }}>
                            <TableContainer
                                sx={{
                                    '&.MuiTableContainer-root': {
                                        '.MuiTable-root': {
                                            '.MuiTableBody-root .MuiTableRow-root': {
                                                '&:hover': {
                                                    '.MuiTableCell-root': {
                                                        background: '#f0f0f0'
                                                    }
                                                }
                                            },
                                            '.MuiTableCell-head': {
                                                fontWeight: '500',
                                                color: '#7D7D82',
                                                lineHeight: '1.38095'
                                            },
                                            '.MuiTableCell-root': {
                                                padding: '16px',
                                                '.MuiAvatar-root': {
                                                    width: '32px',
                                                    height: '32px'
                                                },
                                                // '.MuiAvatar-img': {}

                                                '.disconnect': {
                                                    color: '#7D7D82',
                                                    background: 'transparent'
                                                }
                                            }
                                        }
                                    }
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Added by</TableCell>
                                            <TableCell>Status </TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading || hasData ? (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Typography
                                                        color="#7D7D82"
                                                        fontSize="0.875rem"
                                                        lineHeight="1.38095"
                                                        fontWeight="500"
                                                        sx={{ textAlign: 'center' }}
                                                    >
                                                        There are currently no connected email addresses.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            data?.getAuthorizedUsers?.data?.users?.map((item) => {
                                                return (
                                                    <TableRow>
                                                        <TableCell>
                                                            <Typography
                                                                color="#00214D"
                                                                fontWeight="600"
                                                                fontSize="0.875rem"
                                                                lineHeight="1.38095"
                                                            >
                                                                {item.email}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" alignItems="center" gap="10px">
                                                                <Avatar alt="User" src={url ?? UserIcon} />
                                                                <Typography color="#7D7D82" fontSize="0.875rem" lineHeight="1.38095">
                                                                    {data?.getAuthorizedUsers?.data?.adminName ?? ''}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography color="#7D7D82" fontSize="0.875rem" lineHeight="1.38095">
                                                                {item.hasUserPermission ? 'Active' : 'Pending'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {isCompanyCreator && (
                                                                <Button
                                                                    variant="text"
                                                                    className="cont"
                                                                    onClick={() => {
                                                                        setEmail(item.email);
                                                                        handleModalOpen();
                                                                    }}
                                                                >
                                                                    Disconnect
                                                                </Button>
                                                            )}
                                                            {/* <Typography color="#7D7D82" fontSize="0.875rem" lineHeight="1.38095" fontWeight="500">
                                                    Disconnect
                                                </Typography> */}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <PermisssionValidationWrapper pageName={PermissionTypes.emailExtension}>
                                {isEmailConnected && isCompanyCreator && (
                                    <Stack
                                        alignItems="flex-start"
                                        sx={{
                                            padding: '16px'
                                        }}
                                    >
                                        <Button
                                            variant="text"
                                            sx={{
                                                padding: '4px 8px',
                                                color: '#FBB414',
                                                svg: {
                                                    path: {
                                                        fill: '#FBB414'
                                                    }
                                                }
                                            }}
                                            startIcon={<EmailPlusIcon />}
                                            onClick={onClickSignIn}
                                        >
                                            Connect an email
                                        </Button>
                                    </Stack>
                                )}
                            </PermisssionValidationWrapper>
                        </Box>
                    </Stack>
                )}
            </Stack>
            <Modal
                open={modalOpen}
                onClose={handleModalClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={modalStyle}>
                    <Stack gap={4}>
                        <Typography variant="h6" component="h2" color="#00214D" fontSize="1.25rem" fontWeight="400" fontFamily="Poppins">
                            Disconnect email
                        </Typography>
                        <Typography component="p" fontSize="0.875rem" color="#00214D">
                            If you disconnect, you and your team will no longer be able to collect email information via the Diolog
                            extension.
                        </Typography>
                        <PermisssionValidationWrapper pageName={PermissionTypes.emailExtension}>
                            <Stack direction="row" justifyContent="flex-end" gap="10px">
                                <Button variant="outlined" className="alternate-reversed" onClick={handleModalClose} disableRipple>
                                    Cancel
                                </Button>
                                <Button className="alternate" sx={{ fontSize: '1rem' }} disableRipple onClick={onDisconnect}>
                                    Disconnect
                                </Button>
                            </Stack>
                        </PermisssionValidationWrapper>
                    </Stack>
                </Box>
            </Modal>
            <Outlet />
        </>
    );
};

export default EmailIntegrationComponent;
